/* Pasting a range copied out of GTO Wizard.
   The solver writes combos and this app writes hand classes, so the paste is converted on
   its way into the field. It stays a paste: it lands at the cursor, and the field's own
   input handler carries it to the preview exactly as typing would. There is no mode and no
   button — text that names a combo by its suits, or weighs a hand after a colon, can only
   have come from over there. */

import { looksLikeGtoWizard, readGtoWizard } from "@preflop/core";
import { flash } from "./flash";

/** Text into a textarea at the cursor, with a comma if it would run into what is there. */
function insert(ta: HTMLTextAreaElement, text: string): void {
  const start = ta.selectionStart ?? ta.value.length;
  const end = ta.selectionEnd ?? start;
  const before = ta.value.slice(0, start).trimEnd().slice(-1);
  const after = ta.value.slice(end).trimStart().charAt(0);
  const piece = (before && before !== "," ? "," : "") + text + (after && after !== "," ? "," : "");
  ta.setRangeText(piece, start, end, "end");
  // The same event typing raises, so the draft, the preview and the chart all follow.
  ta.dispatchEvent(new Event("input", { bubbles: true }));
}

/** How often hero holds each hand in the chart being written, when that is known. */
export type Arriving = () => ReadonlyMap<string, number> | null;

/** Convert a GTO Wizard range on its way into a notation field. Other pastes are left alone. */
function onPaste(ev: ClipboardEvent, ta: HTMLTextAreaElement, arriving?: Arriving): void {
  const src = ev.clipboardData?.getData("text") ?? "";
  if (!looksLikeGtoWizard(src)) return;
  let read: ReturnType<typeof readGtoWizard>;
  try {
    read = readGtoWizard(src, arriving?.());
  } catch (e) {
    // Pasted raw all the same: something that looks like a solver range and does not parse
    // is worth seeing, and the reason is on the line under the chart.
    flash(
      "That looks like a GTO Wizard range, but " + (e instanceof Error ? e.message : String(e)),
      "err",
    );
    return;
  }
  ev.preventDefault();
  if (!read.note) return flash(`GTO Wizard: ${read.combos} combos read, none above 0.05%.`, "err");
  insert(ta, read.note);
  flash(
    [
      `GTO Wizard: ${read.combos} combos read, ${read.hands} hands written` +
        // Said when it happened, because it is the difference between the two formats and
        // the numbers in the field are not the numbers that were pasted.
        (read.scaled ? `, ${read.scaled} read against the range hero arrives with.` : "."),
      ...read.warn,
    ].join(" · "),
    "info",
  );
}

/** Every notation field on the page takes a solver range this way. */
export function takesGtoWizard(ta: HTMLTextAreaElement, arriving?: Arriving): HTMLTextAreaElement {
  ta.addEventListener("paste", (ev) => onPaste(ev, ta, arriving));
  return ta;
}
